/* =========================================================================
   JOMIKUTO · predictive-search.js
   <jomi-predictive-search> custom element. Wraps a search input.
   On typing (debounced 200ms), hits Shopify's /search/suggest.json and
   renders up to 6 product results inline. Keyboard nav with arrow keys.
   ========================================================================= */

class JomiPredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    this.resultsEl = this.querySelector('[data-jomi-predictive-results]');
    this.closeBtn = this.querySelector('[data-jomi-predictive-close]');
    this.overlay = this.querySelector('[data-jomi-predictive-overlay]');
    if (!this.input || !this.resultsEl) return;

    this.openTrigger = document.querySelectorAll('[data-jomi-search-trigger]');

    // Open/close
    this.openTrigger.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
    });
    this.closeBtn?.addEventListener('click', () => this.close());
    this.overlay?.addEventListener('click', () => this.close());
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) this.close();
    });

    // Debounced typing
    let timer;
    this.input.addEventListener('input', (e) => {
      clearTimeout(timer);
      const q = e.target.value.trim();
      if (q.length < 2) {
        this.resultsEl.innerHTML = '';
        return;
      }
      timer = setTimeout(() => this.#fetch(q), 200);
    });

    // Keyboard nav: arrow keys move through results, Enter follows link
    this.input.addEventListener('keydown', (e) => {
      const items = this.resultsEl.querySelectorAll('[data-jomi-result]');
      if (items.length === 0) return;
      const active = this.resultsEl.querySelector('.is-active');
      let idx = active ? [...items].indexOf(active) : -1;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        idx = Math.min(idx + 1, items.length - 1);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        idx = Math.max(idx - 1, 0);
      } else if (e.key === 'Enter' && active) {
        e.preventDefault();
        active.querySelector('a').click();
      } else {
        return;
      }
      items.forEach(i => i.classList.remove('is-active'));
      items[idx].classList.add('is-active');
      items[idx].scrollIntoView({ block: 'nearest' });
    });
  }

  isOpen() { return this.getAttribute('data-open') === 'true'; }
  open() {
    this.setAttribute('data-open', 'true');
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(() => this.input?.focus());
  }
  close() {
    this.setAttribute('data-open', 'false');
    document.body.style.overflow = '';
    if (this.input) this.input.value = '';
    this.resultsEl.innerHTML = '';
  }

  async #fetch(q) {
    try {
      this.resultsEl.innerHTML = `<p class="jomi-predictive__loading">Searching…</p>`;
      const url = `/search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product,article,page&resources[limit]=6&resources[options][unavailable_products]=last`;
      const res = await fetch(url);
      const data = await res.json();
      const { products, articles, pages } = data.resources.results;
      if (products.length === 0 && articles.length === 0 && pages.length === 0) {
        this.resultsEl.innerHTML = `<p class="jomi-predictive__empty">No matches for <em>${this.#escape(q)}</em>. Try the full search →</p>`;
        return;
      }
      this.#render(products, articles, pages, q);
    } catch (err) {
      console.error('[PredictiveSearch]', err);
    }
  }

  #render(products, articles, pages, q) {
    let html = '';
    if (products.length) {
      html += `<div class="jomi-predictive__group"><p class="eyebrow-sm">Products</p><ul>`;
      html += products.map(p => `
        <li data-jomi-result>
          <a href="${p.url}">
            ${p.featured_image?.url ? `<span class="jomi-predictive__thumb"><img src="${p.featured_image.url}&width=200" alt="${this.#escape(p.title)}" loading="lazy"></span>` : '<span class="jomi-predictive__thumb"></span>'}
            <span class="jomi-predictive__body">
              <span class="jomi-predictive__title">${this.#escape(p.title)}</span>
              <span class="jomi-predictive__meta">${p.price_min && p.price_min !== p.price_max ? 'From ' : ''}${this.#money(p.price_min || p.price)}</span>
            </span>
          </a>
        </li>`).join('');
      html += `</ul></div>`;
    }
    if (articles.length) {
      html += `<div class="jomi-predictive__group"><p class="eyebrow-sm">Journal</p><ul>`;
      html += articles.map(a => `
        <li data-jomi-result>
          <a href="${a.url}"><span class="jomi-predictive__body"><span class="jomi-predictive__title">${this.#escape(a.title)}</span></span></a>
        </li>`).join('');
      html += `</ul></div>`;
    }
    if (pages.length) {
      html += `<div class="jomi-predictive__group"><p class="eyebrow-sm">Pages</p><ul>`;
      html += pages.map(p => `<li data-jomi-result><a href="${p.url}"><span class="jomi-predictive__body"><span class="jomi-predictive__title">${this.#escape(p.title)}</span></span></a></li>`).join('');
      html += `</ul></div>`;
    }
    html += `<p class="jomi-predictive__all"><a href="/search?q=${encodeURIComponent(q)}">View all results for <em>${this.#escape(q)}</em> →</a></p>`;
    this.resultsEl.innerHTML = html;
  }

  #money(cents) {
    return Jomi.formatMoney(cents);
  }
  #escape(str) { return (str || '').replace(/[<>&"]/g, c => ({'<':'&lt;','>':'&gt;','&':'&amp;','"':'&quot;'}[c])); }
}

if (!customElements.get('jomi-predictive-search')) {
  customElements.define('jomi-predictive-search', JomiPredictiveSearch);
}
